import React, { useState, forwardRef, useImperativeHandle, useContext, useMemo } from "react";
import { useReconciliationManagmentForTransactionsHook } from "../../hooks/useReconciliationManagmentForTransactionsHook";
import { useCalculateComponentHeightSizeHook, useHandleDataHook } from "../../../../hooks";
import { CircleAnimationCompact } from "../../../loading";
import { Button, message, Modal, Space, Splitter } from "antd";
import { includePercentageAtInvoicesData } from "../../utils";
import { BankReconciliationTable, MiniTableInvoices } from "../reconciliation";
import PropTypes from "prop-types";
import { CompaniesContext } from "../../../../contexts";
import { bank_reconciliation_manager } from "../../../../services";

export const BankReconciliationForTransactionModal = forwardRef(({
	token, 
	bankAccountSelected = {},
	bankTransactionsData = [],
	onUpdateTransactionsData = () => {},
}, ref) => {	
	const { selectedCompany: company } = useContext(CompaniesContext);
	const [ loadingInvoices, setLoadingInvoices ] = useState(false);
	const { tableHeight: tableHeightForSplitter } = useCalculateComponentHeightSizeHook({ heightDifference: 275 });
		
	const [lastInvoicesId, setLastInvoicesId] = useState({
		in: null,
		out: null,
	});
	
	const {
		data: invoicesData,
		dataBackup: invoicesBackupData,
		searchText: searchInvoiceText,
		setAllData: setAllInvoicesData,
		clearAllData: clearInvoicesData,
		addMoreData: addMoreInvoicesData,
		updateMultipleData: updateMultipleInvoicesData,
		onChangeSearch: onChangeInvoicesSearch,
		updateBackupData: updateInvoicesBackupData,
		onFilterByProperty: onFilterInvoicesByProperty,
	} = useHandleDataHook();
	
	const {		
		selectedTransaction,
		invoicesToSend,
		filterByPosition,
		filterByState,
		invoiceRegistersToDelete,		
		setFilterByPosition,
		setFilterByState,
		setSelectedTransaction,
		setInvoicesToSend,
		processInvoiceToSend,
		updateInvoiceAmount,
		setInvoiceRegistersToDelete,
		changeSelectedTransaction,
		fetchSendReconciliation,
		contextHolderFailedInvoices,
	} = useReconciliationManagmentForTransactionsHook(invoicesData, bankAccountSelected.currency, true);
	
	const {
		id: idTransaction,
		reconciliation: { invoices: originalInvoices = {} } = {},
	} = selectedTransaction;
	
	const fetchInvoicesForReconciliation = async (transaction) => {
		if(invoicesData.length !== 0) { // Si no esta vacio, cambiar el tipo de transaccion actualizado
			changeSelectedTransaction(transaction, invoicesBackupData, onFilterInvoicesByProperty);
			return;
		}
		
		try {
			const data = {
				bank_account_id: bankAccountSelected.id,
				company_id: company.id,
				get_all_invoices: true,
			};

			setLoadingInvoices(true);
			const res = await bank_reconciliation_manager(token, "invoice", data);
			if(!res){
				setLoadingInvoices(false);
				return;
			}

			const { invoices, lastInvoiceOutId = null, lastInvoiceInId = null } = res;
			const refactorizedTransactionsData = includePercentageAtInvoicesData(invoices);
			changeSelectedTransaction(transaction, refactorizedTransactionsData, onFilterInvoicesByProperty);
			setAllInvoicesData(refactorizedTransactionsData);
			setLastInvoicesId({ in: lastInvoiceInId, out: lastInvoiceOutId });
		} catch (err) {
			message.error(err.response.data);
		} finally {
			setLoadingInvoices(false);
		}		
	};
	
	const updateReconciliationTransactionInFetch = (updatedTransactionInvoices, remainingAmount) => {				
		// ! ==> Now its time to update at the UI
		const updatedData = bankTransactionsData.map((transaction) => {
			// idTransaction
			if(transaction.id === idTransaction){
				const totalReconciliation = Object.values(updatedTransactionInvoices).reduce(
					(accumulator, item) => accumulator += item.amount
					, 0);
				
				return {
					...transaction,
					reconciliation: {
						invoices: updatedTransactionInvoices,
						transaction_consumed: remainingAmount === 0.00,
					},
					reconciliation_total: totalReconciliation,
				};
			}						
			return {...transaction};
		});
						
		onUpdateTransactionsData(updatedData);
		setSelectedTransaction({...selectedTransaction, reconciliation: { // ? Update the selected Transaction
			invoices: updatedTransactionInvoices,
			transaction_consumed: remainingAmount === 0.00,
		}});
	};
	
	const updateReconciliationInvoicesInFetch = (invoices, invoicesToUpdate, failedInvoicesRegisters) => {
		// ! ==> Now its time to update at the UI
		addMoreInvoicesData(includePercentageAtInvoicesData(invoices)); // ? Set the new invoices
		updateInvoicesBackupData();	// ? Reset the original data
		onChangeInvoicesSearch(searchInvoiceText); // ? Reset the data for the research
		updateMultipleInvoicesData(invoicesToUpdate);
		setInvoicesToSend((prevInvoices) =>
			Object.fromEntries(
				Object.entries(prevInvoices).map(([invoiceId, invoiceData]) => [
					invoiceId,
					failedInvoicesRegisters.includes(invoiceId)
						? invoiceData
						: {
							...invoiceData,
							initial_pendient: invoiceData.pendient,
						},
				])
			)
		);
	};

	useImperativeHandle(ref, () => ({
		onSetTransaction: async(value) => {
			setSelectedTransaction(value);
			await fetchInvoicesForReconciliation(value);
		},
		clearInvoices: () => clearInvoicesData(),
	}));

	const filterInvoiceOptions = useMemo(() => ({
		positionType: filterByPosition,
		onPositionType: (value) => {
			setFilterByPosition(value);
			onFilterInvoicesByProperty("position", value);
		},
		payedState: filterByState,
		onPayedState: (value) => {
			setFilterByState(value);
			onFilterInvoicesByProperty("payedState", value);
		},
	}), [filterByPosition, onFilterInvoicesByProperty]);
	
	const ReconciliationSplitterView = (
		<Splitter
			layout="vertical"
			style={{
				height: tableHeightForSplitter,
				boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
			}}
		>
			<Splitter.Panel style={{ flex: 1, display: "flex", flexDirection: "column" }}>
				<br/>
				{/* RECONCILIATION TABLE VIEW */}
				<BankReconciliationTable
					invoicesToSend={invoicesToSend}
					bankTransaction={selectedTransaction}
					currency={bankAccountSelected.currency}									
					onUpdateInvoiceAmount={updateInvoiceAmount}
					onRemoveInvoice={({ invoiceId, checked }) => {
						processInvoiceToSend({invoiceId, checked});
						if(!originalInvoices[invoiceId]) return;
						setInvoiceRegistersToDelete([...invoiceRegistersToDelete, invoiceId]);
					}}
					isEditable={true}
				/>
			</Splitter.Panel>
			<Splitter.Panel style={{ flex: 1, display: "flex", flexDirection: "column" }}>
				{/* INVOICES VIEW */}
				<MiniTableInvoices
					token={token}
					invoices={invoicesData}
					invoicesToSend={invoicesToSend}
					companyId={company.id}
					bankAccountId={bankAccountSelected.id}
					enableSelectInvoices={true}
					searchText={searchInvoiceText}					
					lastInvoicesId={lastInvoicesId}
					filterOptions={filterInvoiceOptions}
					onSendInvoice={({ invoiceId, checked, inInvoicePaid, outInvoicePaid }) => {
						processInvoiceToSend({invoiceId, checked, inInvoicePaid, outInvoicePaid});
						if(!originalInvoices[invoiceId]) return;
						setInvoiceRegistersToDelete([...invoiceRegistersToDelete, invoiceId]);
					}}
					onInvoices={(invoices) => setAllInvoicesData(invoices)}
					onChangeSearch={onChangeInvoicesSearch}
					onSendLastInvoices={setLastInvoicesId}
				/>
			</Splitter.Panel>
		</Splitter>
	);
	
	return (
		<Modal
			title={"Reconciliación Bancaria"}
			open={JSON.stringify(selectedTransaction) !== "{}"}
			onCancel={() => {
				setSelectedTransaction({});
				setInvoicesToSend({});
				setInvoiceRegistersToDelete([]);
			}}
			width="60vw"
			footer={() => (
				<Space>
					<Button onClick={() => {
						setSelectedTransaction({});
						setInvoicesToSend({});
						setInvoiceRegistersToDelete([]);
					}}>Regresar</Button>
					<Button
						loading={loadingInvoices}
						onClick={async() => {										
							await fetchSendReconciliation({
								token,
								companySelectedId: company.id,
								onLoading: setLoadingInvoices,
								onUpdateReconciliationInvoicesInFetch: updateReconciliationInvoicesInFetch,
								onUpdateReconciliationTransactionInFetch: updateReconciliationTransactionInFetch,
							});
						}}									
						type="primary"
					>Guardar Cambios</Button>
				</Space>
			)}
		>
			{loadingInvoices ? <CircleAnimationCompact/> : ReconciliationSplitterView}
			{contextHolderFailedInvoices}
		</Modal>
	);
});

BankReconciliationForTransactionModal.displayName = "BankReconciliationForTransactionModal";

BankReconciliationForTransactionModal.propTypes = {
	token: PropTypes.string.isRequired, 
	bankAccountSelected: PropTypes.object,
	bankTransactionsData: PropTypes.array,
	onUpdateTransactionsData: PropTypes.func,
};